import React from 'react';
import { motion } from 'framer-motion';
import { Cookie, Shield, Settings, BarChart3, Globe, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { contactInfo } from '../config/contactInfo';

const CookiePolicy: React.FC = () => {
  const cookieTypes = [
    {
      icon: Shield,
      title: 'Strictly Necessary Cookies',
      canDisable: false,
      description: 'These cookies are essential for the website and Services to function correctly. They cannot be disabled.',
      cookies: [
        { name: 'Firebase Auth Session', purpose: 'Maintains your logged-in session and authentication state', duration: 'Session / persistent (up to 14 days)', provider: 'Google Firebase' },
        { name: 'CSRF Token', purpose: 'Protects against cross-site request forgery attacks', duration: 'Session', provider: 'BioAxis' },
        { name: 'Cookie Consent', purpose: 'Stores your cookie preference choices', duration: '12 months', provider: 'BioAxis' },
      ]
    },
    {
      icon: BarChart3,
      title: 'Analytics & Performance Cookies',
      canDisable: true,
      description: 'These cookies help us understand how visitors interact with our website, allowing us to improve our Services.',
      cookies: [
        { name: 'Google Analytics (_ga, _gid)', purpose: 'Tracks page views, session duration, and user interactions to improve our website', duration: '_ga: 2 years, _gid: 24 hours', provider: 'Google LLC' },
        { name: 'Firebase Analytics', purpose: 'Monitors application performance and usage patterns', duration: 'Up to 14 months', provider: 'Google Firebase' },
      ]
    },
    {
      icon: Settings,
      title: 'Functional Cookies',
      canDisable: true,
      description: 'These cookies enable enhanced functionality and personalisation, such as remembering your preferences.',
      cookies: [
        { name: 'Theme Preference', purpose: 'Remembers your dark/light mode preference', duration: 'Persistent (localStorage)', provider: 'BioAxis' },
        { name: 'Language Preference', purpose: 'Stores your preferred language setting', duration: 'Persistent (localStorage)', provider: 'BioAxis' },
      ]
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20" style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Cookie className="w-16 h-16 text-primary-400 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Cookie <span className="gradient-text">Policy</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              This policy explains how BioAxis uses cookies and similar technologies on our website and Services, in compliance with the Electronic Communications and Transactions Act 25 of 2002.
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: February 2026
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* What Are Cookies */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mb-12"
          >
            <h2 className="text-3xl font-bold mb-6">What Are Cookies?</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                Cookies are small text files that are placed on your device (computer, tablet, or mobile phone) when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and supply information to website owners.
              </p>
              <p>
                We also use similar technologies such as localStorage and sessionStorage (collectively referred to as "cookies" in this policy) to store preferences and session data locally on your device.
              </p>
              <p>
                In terms of the ECT Act and POPIA, we are required to inform you about the cookies we use and, where appropriate, obtain your consent before placing non-essential cookies on your device.
              </p>
            </div>
          </motion.div>

          {/* Cookie Categories */}
          <div className="space-y-8">
            {cookieTypes.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-effect glass-effect-hover p-8 rounded-2xl"
              >
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div className="flex items-start gap-4">
                    <category.icon className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-2xl font-bold">{category.title}</h3>
                      <p className="text-gray-400 text-sm mt-1">{category.description}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-3 py-1 rounded-full flex-shrink-0 ${
                    category.canDisable
                      ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-700/30'
                      : 'bg-green-900/30 text-green-400 border border-green-700/30'
                  }`}>
                    {category.canDisable ? 'Optional' : 'Required'}
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-dark-700/50">
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Cookie</th>
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Purpose</th>
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Duration</th>
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Provider</th>
                      </tr>
                    </thead>
                    <tbody>
                      {category.cookies.map((cookie, idx) => (
                        <tr key={idx} className="border-b border-dark-800/50">
                          <td className="py-3 px-2 text-primary-300 font-medium">{cookie.name}</td>
                          <td className="py-3 px-2 text-gray-300">{cookie.purpose}</td>
                          <td className="py-3 px-2 text-gray-400">{cookie.duration}</td>
                          <td className="py-3 px-2 text-gray-400">{cookie.provider}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Third-Party Cookies */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-12"
          >
            <div className="flex items-start gap-4 mb-6">
              <Globe className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
              <h3 className="text-2xl font-bold">Third-Party Services</h3>
            </div>
            <div className="text-gray-300 space-y-4">
              <p>
                Our Services integrate with third-party providers that may set their own cookies. These include:
              </p>
              <ul className="space-y-3">
                {[
                  'Google Firebase: Authentication, database, and hosting services',
                  'PayFast: Payment processing (cookies set during the checkout process on PayFast\'s domain)',
                  'Google Analytics: Website analytics and performance monitoring',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-400 mt-4">
                We do not control cookies set by third parties. Please refer to their respective privacy and cookie policies for more information.
              </p>
            </div>
          </motion.div>

          {/* Managing Cookies */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <Settings className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
              <h3 className="text-2xl font-bold">Managing Your Cookie Preferences</h3>
            </div>
            <div className="text-gray-300 space-y-4">
              <p>
                You have the right to control which cookies are placed on your device. You can manage your preferences in the following ways:
              </p>
              <ul className="space-y-3">
                {[
                  'Browser settings: Most browsers allow you to block or delete cookies through their settings. Note that blocking essential cookies may prevent our Services from functioning correctly.',
                  'Browser extensions: Privacy-focused browser extensions can provide additional cookie management capabilities.',
                  'Opt-out links: Google Analytics opt-out is available via the Google Analytics Opt-out Browser Add-on.',
                  'Device settings: Mobile devices offer cookie management options in their privacy settings.',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-400 mt-4">
                Please note that disabling certain cookies may affect the functionality of our website and Services.
              </p>
            </div>
          </motion.div>

          {/* Updates */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <Clock className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
              <h3 className="text-2xl font-bold">Changes to This Cookie Policy</h3>
            </div>
            <div className="text-gray-300 space-y-4">
              <p>
                We may update this Cookie Policy from time to time to reflect changes in the cookies we use, our technology, or legal requirements. Any material changes will be communicated through a notice on our website.
              </p>
              <p>
                We encourage you to review this page periodically to stay informed about our use of cookies.
              </p>
            </div>
          </motion.div>

          {/* Related Policies */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-8"
          >
            <h3 className="text-2xl font-bold mb-6">Related Policies</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { name: 'Privacy Policy', href: '/privacy', desc: 'How we collect and protect your personal information' },
                { name: 'POPIA Compliance', href: '/popia', desc: 'Your data protection rights under POPIA' },
              ].map((policy) => (
                <Link
                  key={policy.href}
                  to={policy.href}
                  className="block p-4 rounded-xl bg-dark-800/50 hover:bg-dark-700/50 border border-dark-700/50 hover:border-primary-500/30 transition-all duration-200"
                >
                  <p className="font-semibold text-primary-400">{policy.name}</p>
                  <p className="text-sm text-gray-400 mt-1">{policy.desc}</p>
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-12 text-center"
          >
            <h3 className="text-2xl font-bold mb-4">Questions About Cookies?</h3>
            <p className="text-gray-300 mb-6">
              If you have any questions about our use of cookies, please contact our privacy team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href={`mailto:${contactInfo.email.privacy}`}
                whileHover={{ scale: 1.05 }}
                className="btn-primary"
              >
                Email Privacy Team
              </motion.a>
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                className="btn-outline"
              >
                Contact Us
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;
